import {
  getSnapshot, Instance, resolveIdentifier, types,
} from 'mobx-state-tree';
import moment, { Moment } from 'moment';
import { DateTime } from 'luxon';
import { Coordinates } from 'viewport-mercator-project';
import getFormattedDistance from '../../../helpers/getFormattedDistance';
import timestampToHrsMin from '../../../helpers/timestampToHrsMin';
import { RouteTimelineDtoModelBase } from './RouteTimelineDtoModel.base';
import getAppRoot from '../utils/getAppRoot';
import {
  defaultDateTimeFormat,
  defaultTimeFormat,
  twelveHrsMinFormat,
  formatDisplayDateTime,
} from '../../../constants';
import { RouteTimelineLogDtoModelType } from './RouteTimelineLogDtoModel';
import { RouteDtoModel, RouteDtoModelType } from './RouteDtoModel';
import { VisitLocationDtoModelType } from './VisitLocationDtoModel';
import { CompleteStopStatus } from './CompleteStopStatusEnum';
import { DistanceUnit } from './DistanceUnitEnum';
import { DateTimeFormat } from './DateTimeFormatEnum';
import { VisitDtoModel } from './VisitDtoModel';
import { safeMstGqlRef } from '../utils/safeMstGqlRef';

const visitRef = safeMstGqlRef(types.late((): any => VisitDtoModel), 'VisitDto');

const sortTimelineLogsByDateDesc = (a: RouteTimelineLogDtoModelType, b: RouteTimelineLogDtoModelType) => {
  if (a.date && b.date) {
    return (b.date > a.date) ? 1 : -1;
  }
  return 0;
};

/* The TypeScript type of an instance of RouteTimelineDtoModel */
export interface RouteTimelineDtoModelType extends Instance<typeof RouteTimelineDtoModel.Type> {
}

/* A graphql query fragment builders for RouteTimelineDtoModel */
export {
  selectFromRouteTimelineDto, routeTimelineDtoModelPrimitives, RouteTimelineDtoModelSelector,
} from './RouteTimelineDtoModel.base';

/**
 * RouteTimelineDtoModel
 */
export const RouteTimelineDtoModel = RouteTimelineDtoModelBase
  .props({
    visit: types.union(types.undefined, types.null, visitRef),
    orders: types.union(types.undefined, types.null, types.array(visitRef)),
  })
  .actions((self) => ({
    afterAttach() {
      const snap = getSnapshot(self);
      if (self.orders && snap.orders?.length) {
        const ids = [...snap.orders];
        self.orders.replace([]);
        self.orders.replace(ids);
      }
      if (snap.visit != null) {
        self.visit = undefined;
        self.visit = snap.visit as typeof self.visit;
      }
    },
  }))
  .views((self) => ({
    get isSelected(): boolean {
      return getAppRoot(self).ui.selectedRouteEvent?.id === self.id || !!getAppRoot(self).mapView.selectedItems?.stops?.find(s => s.id === self.id);
    },
    get isHovered(): boolean {
      return getAppRoot(self).ui.hoveredRouteEvent?.id === self.id;
    },
    get isRouteSelected(): boolean {
      return getAppRoot(self).ui.selectedRoute?.id === self.orders?.[0]?.route?.id;
    },
    get isRouteHovered(): boolean {
      return getAppRoot(self).ui.hoveredRoute?.id === self.id;
    },
    get isStartEvent(): boolean {
      return self.eventType === 'leaving_start_location';
    },
    get isEndEvent(): boolean {
      return self.eventType === 'reaching_end_location';
    },
    get isStartEndEvent(): boolean {
      return this.isStartEvent || this.isEndEvent;
    },
    get isIdleEvent(): boolean {
      return self.eventType === 'idling';
    },
    get isBreakEvent(): boolean {
      return self.eventType === 'paused';
    },
    get isStopEvent(): boolean {
      return self.eventType === 'delivering_stop';
    },
    get getDepartedLog(): RouteTimelineLogDtoModelType | undefined {
      return self.logs?.find((l) => l?.status === CompleteStopStatus.departed);
    },
    get location(): VisitLocationDtoModelType | undefined {
      if (this.isStartEvent) {
        return this.route?.constraints?.startLocations?.[0];
      }

      if (this.isEndEvent) {
        return this.route?.constraints?.endLocations?.[0];
      }

      return self.orders?.[0]?.activeLocation;
    },
    get coordinates(): Coordinates | undefined {
      return this.location ? [this.location.longitude, this.location.latitude] as Coordinates : undefined;
    },
    get route(): RouteDtoModelType | undefined {
      if (!getAppRoot(self)?.sortedRoutes) {
        return undefined;
      }

      if (this.isStopEvent) {
        return getAppRoot(self).sortedRoutes
          .find((route: RouteDtoModelType) => route.stops?.[self.id] != null
          && resolveIdentifier(RouteDtoModel, getAppRoot(self), route.id));
      }

      return getAppRoot(self).sortedRoutes
        .find((route: RouteDtoModelType) => route.timeline?.find((e) => e.id === self.id)
          && resolveIdentifier(RouteDtoModel, getAppRoot(self), route.id));
    },
    get currentStatus(): RouteTimelineLogDtoModelType | undefined {
      const departedLog = this.getDepartedLog;
      if (departedLog) {
        return departedLog;
      }

      const stopLogs = self.logs
        ?.filter((l) => l.status !== 'done' && l.status !== 'skipped')
        .sort(sortTimelineLogsByDateDesc);
      return stopLogs?.length ? stopLogs[0] : undefined;
    },
    get startTimeMoment(): Moment | undefined {
      let { startTime } = self;

      if (this.isStartEvent) {
        startTime = self.departureTime;
      } else if (this.isEndEvent) {
        startTime = self.arrivalTime;
      }

      return startTime ? moment(startTime, defaultDateTimeFormat) : undefined;
    },
    get endTimeMoment(): Moment | undefined {
      if (this.isStartEvent && self.departureTime) {
        return moment(self.departureTime, defaultDateTimeFormat);
      } if (this.isEndEvent && self.arrivalTime) {
        return moment(self.arrivalTime, defaultDateTimeFormat);
      } if (this.isStopEvent && self.startTime && self.endTime) {
        const startTimeMoment = moment(self.startTime, defaultDateTimeFormat);
        const endTimeMoment = moment(self.endTime, defaultDateTimeFormat);
        return startTimeMoment.add(moment.duration(endTimeMoment.diff(startTimeMoment)).asMinutes() || 1, 'minute');
      } if (this.isIdleEvent || this.isBreakEvent) {
        return moment(self.endTime, defaultDateTimeFormat);
      }
      return undefined;
    },
    get dateTimeFormat(): any {
      return getAppRoot(self).organization.settings?.dateTimeFormat;
    },
    get formattedStartDateTime(): string | undefined {
      if (!self.startTime) return undefined;
      
      const parsed = DateTime.fromISO(self.startTime, { setZone: true });
      if (!parsed.isValid) return undefined;
      
      return formatDisplayDateTime(parsed, this.dateTimeFormat);
    },
    get startTimeString(): string {
      return getAppRoot(self).organization.settings.dateTimeFormat === DateTimeFormat.MILITARY ? timestampToHrsMin(self.startTime, defaultTimeFormat) : timestampToHrsMin(self.startTime, twelveHrsMinFormat);
    },
    get endTimeString(): string {
      return getAppRoot(self).organization.settings.dateTimeFormat === DateTimeFormat.MILITARY ? timestampToHrsMin(self.endTime, defaultTimeFormat) : timestampToHrsMin(self.endTime, twelveHrsMinFormat);
    },
    // Mirrors the backend move rule: reordering closes at arrival, not at the point the stop
    // becomes next in the sequence. A driver who is merely on the way can still be re-routed.
    get isDraggable(): boolean {
      return (!(self.isArrived || self.isDeparted)) && this.isStopEvent
        && (self.orders == null || self.orders.length > 0);
    },
    get convertedDistance(): number | undefined {
      return getAppRoot(self).organization.settings.distanceUnit === DistanceUnit.KM ? this.kmFromDistance : this.milesFromDistance;
    },
    get kmFromDistance(): number | undefined {
      return getFormattedDistance(self.distance, DistanceUnit.KM, 3);
    },
    get milesFromDistance(): number | undefined {
      return getFormattedDistance(self.distance, DistanceUnit.MILES, 3);
    },
  }));
